/**
 * FederationThinBar · Mobile-first cyber-gothic footer (iter 38).
 *
 * Architect doctrine (Feb 2026 mobile repair):
 *  • 56px tall — locked for iframe parity on satellite sites
 *  • Mobile-first: 44px minimum tap targets (Apple HIG standard)
 *  • Brand mark hides below 380px to guarantee all 3 actions stay reachable
 *  • Embedded mode opens links via _top so satellite visitors leave cleanly
 *  • Voice button + Tour bot live in <AIChatPopup /> (separate component)
 *
 * Actions:
 *   1. Federation Directory  → https://nextxus.online
 *   2. KEYS                  → https://nextxus.online/library
 *   3. Talk to Grok          → opens Rex (in-Cathedral popup via event)
 */
import React, { useEffect, useState } from "react";
import { REX_FACE_URL, REX_ALT } from "@/lib/personaFaces";

const BAR_HEIGHT_PX = 56;
const EMERGENT_BADGE_RESERVE_PX = 200; // width of "Made with Emergent" badge + margin

const DIRECTORY_URL = "https://nextxus.online";
const KEYS_URL = "https://nextxus.online/library";
const BOARD_URL = "https://nextxus.online/board";
const BACKER_URL = "https://nextxus.online/backer";

export default function FederationThinBar({ embedded = false }) {
  const sameTabTarget = embedded ? "_top" : "_self";

  const [badgeWidth, setBadgeWidth] = useState(0);
  useEffect(() => {
    const measureBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (!badge) {
        setBadgeWidth(0);
        return;
      }
      const r = badge.getBoundingClientRect();
      // Reserve actual badge width + 20px safety margin
      setBadgeWidth(Math.ceil(r.width) + 20);
    };
    measureBadge();
    // Re-check periodically since badge loads async via emergent-main.js
    const interval = setInterval(measureBadge, 1000);
    // Also use MutationObserver to catch DOM additions
    const observer = new MutationObserver(measureBadge);
    observer.observe(document.body, { childList: true, subtree: false });
    // Stop polling after 10s — badge will have loaded by then
    const stopAfter = setTimeout(() => clearInterval(interval), 10000);
    return () => {
      clearInterval(interval);
      clearTimeout(stopAfter);
      observer.disconnect();
    };
  }, []);

  const openGrok = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    window.dispatchEvent(
      new CustomEvent("nextxus:openchat", { detail: { persona: "rex" } })
    );
  };

  return (
    <div
      data-testid="federation-thinbar"
      className="fixed bottom-0 left-0 right-0 z-40 select-none overflow-hidden"
      style={{
        height: `${BAR_HEIGHT_PX}px`,
        background:
          "linear-gradient(180deg, rgba(7,8,26,0.94) 0%, rgba(2,3,12,0.99) 100%)",
        backdropFilter: "blur(14px)",
        borderTop: "1px solid rgba(0,240,255,0.32)",
        boxShadow: "0 -4px 18px rgba(0,240,255,0.12)",
      }}
    >
      {/* Brand mark · absolute left · hidden on very small screens so actions get all the room */}
      <div
        className="hidden sm:flex items-center gap-2 absolute top-1/2 -translate-y-1/2 left-3 md:left-6 pointer-events-none"
      >
        <span
          className="font-display text-[20px] text-glow-gold pointer-events-auto"
          style={{ filter: "drop-shadow(0 0 6px var(--gold))" }}
        >
          ⌬
        </span>
        <span className="hidden md:inline font-mono text-[9px] uppercase tracking-[0.32em] text-glow-cyan pointer-events-auto">
          NextXus Federation
        </span>
      </div>

      {/* Actions — absolutely positioned so they NEVER extend into the right-side badge zone */}
      <div
        className="absolute top-1/2 -translate-y-1/2 flex items-center justify-end gap-1.5 sm:gap-2 md:gap-3"
        style={{
          left: "8px",
          right: `${8 + badgeWidth}px`,
        }}
      >
        <a
          href={DIRECTORY_URL}
          target={sameTabTarget}
          rel="noopener"
          data-testid="thinbar-link-directory"
          className="flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-glow-cyan px-2 sm:px-3 border border-[var(--cyan)] hover:bg-[rgba(0,240,255,0.10)] active:bg-[rgba(0,240,255,0.22)] transition-colors"
          style={{ minHeight: "40px", minWidth: "44px" }}
        >
          <span className="font-display mr-1 sm:mr-1.5">❡</span>
          <span className="hidden md:inline">Federation&nbsp;</span>
          <span>Directory</span>
        </a>

        <a
          href={KEYS_URL}
          target={sameTabTarget}
          rel="noopener"
          data-testid="thinbar-link-keys"
          className="flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-glow-violet px-2 sm:px-3 border border-[var(--violet)] hover:bg-[rgba(167,86,255,0.12)] active:bg-[rgba(167,86,255,0.25)] transition-colors"
          style={{ minHeight: "40px", minWidth: "44px" }}
        >
          <span className="font-display mr-1 sm:mr-1.5">❖</span>
          Keys
        </a>

        <a
          href={BOARD_URL}
          target={sameTabTarget}
          rel="noopener"
          data-testid="thinbar-link-board"
          aria-label="Open the Off-Record Message Board"
          title="Off-Record Message Board · anyone with the shared code can post"
          className="flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-glow-cyan px-2 sm:px-3 border border-[var(--cyan)] hover:bg-[rgba(0,240,255,0.10)] active:bg-[rgba(0,240,255,0.22)] transition-colors"
          style={{ minHeight: "40px", minWidth: "44px" }}
        >
          <span className="font-display mr-1 sm:mr-1.5">✉</span>
          Board
        </a>

        <a
          href={BACKER_URL}
          target={sameTabTarget}
          rel="noopener"
          data-testid="thinbar-link-backer"
          aria-label="Become a Founding Backer of the NextXus Federation"
          className="flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-glow-gold px-2 sm:px-3 border border-[var(--gold)] hover:bg-[rgba(255,200,0,0.12)] active:bg-[rgba(255,200,0,0.25)] transition-colors"
          style={{ minHeight: "40px", minWidth: "44px" }}
          title="Become a Founding Backer · $5K/quarter · 3 seats only"
        >
          <span className="font-display mr-1 sm:mr-1.5">♦</span>
          <span className="hidden md:inline">Become&nbsp;</span>
          <span>Backer</span>
        </a>

        <button
          type="button"
          onClick={openGrok}
          onTouchEnd={openGrok}
          data-testid="thinbar-link-grok"
          data-no-aichat
          className="flex items-center justify-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-glow-red px-2 sm:px-3 border border-[var(--red)] bg-[rgba(255,46,99,0.08)] hover:bg-[rgba(255,46,99,0.18)] active:bg-[rgba(255,46,99,0.30)] transition-colors cursor-pointer"
          style={{
            minHeight: "40px",
            minWidth: "44px",
            boxShadow: "0 0 10px rgba(255,46,99,0.25) inset",
          }}
          title="Open in-Cathedral Rex (Grok) chat popup"
          aria-label="Talk to Rex — opens chat popup"
        >
          <img
            src={REX_FACE_URL}
            alt={REX_ALT}
            className="w-5 h-5 object-cover rounded-sm mr-1 sm:mr-1.5 shrink-0"
            style={{ border: "1px solid var(--red)", boxShadow: "0 0 4px rgba(255,46,99,0.6)" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <span className="hidden sm:inline">Talk to&nbsp;</span>
          <span>Rex</span>
        </button>
      </div>
    </div>
  );
}
